import { Direction, RegularBid } from "./types";

export function expandSymmetric<T extends { direction: Direction }>(
  rows: T[],
): (Omit<T, "direction"> & { direction: Exclude<Direction, "SYMMETRIC"> })[] {
  const out: any[] = [];
  for (const r of rows) {
    if (r.direction === "SYMMETRIC") {
      out.push({ ...r, direction: "UPWARD" });
      out.push({ ...r, direction: "DOWNWARD" });
    } else {
      out.push(r);
    }
  }
  return out;
}

export function keepLatestByHour<T extends { start_date: string; direction: any; created_at: string }>(rows: T[]): T[] {
  const map = new Map<string, T>();
  for (const r of rows) {
    const hourKey = hourKeyUTC(r.start_date);
    const key = `${hourKey}|${r.direction}`;
    const existing = map.get(key);
    if (!existing || new Date(r.created_at) > new Date(existing.created_at)) {
      map.set(key, r);
    }
  }
  return Array.from(map.values());
}

export const computeAveragePrice = (
  rows: RegularBid[],
  priceField: "accepted_price_eur" | "offered_price_eur",
  qtyField: "accepted_quantity_mw" | "offered_quantity_mw",
) => {
  let num = 0;
  let den = 0;
  for (const r of rows) {
    const price = (r as any)[priceField] as number | undefined;
    const qty = (r as any)[qtyField] as number | undefined;
    if (price != null && qty && qty > 0) {
      num += price * qty;
      den += qty;
    }
  }
  return den > 0 ? num / den : null;
};

export const formatPrice = (p: number | null) => (p == null ? "—" : `${p.toFixed(2)} €`);

export function startOfUtcDay(d: Date): Date {
  const dt = new Date(d);
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 0, 0, 0, 0));
}

/** 72h categories aligned to UTC: yesterday 00:00Z -> tomorrow 23:00Z (exclusive of 24:00Z) */
export function buildThreeDayCategoriesUTC(center: Date): string[] {
  const start = startOfUtcDay(new Date(center));
  const startYesterdayUTC = new Date(start.getTime() - 24 * 3600 * 1000);
  const cats: string[] = [];
  for (let i = 0; i < 72; i++) {
    const t = new Date(startYesterdayUTC.getTime() + i * 3600 * 1000);
    cats.push(t.toISOString());
  }
  return cats;
}

export const hourKeyUTC = (d: string | Date) => {
  const dt = new Date(d);
  const snapped = new Date(dt);
  snapped.setUTCMinutes(0, 0, 0);
  return snapped.toISOString();
};

/** For each UTC-hour key, decide "offered" if any row is PENDING; else "accepted". */
export function computeHourModeByKey(rows: RegularBid[]): Map<string, "offered" | "accepted"> {
  const mode = new Map<string, "offered" | "accepted">();
  for (const r of rows) {
    const key = hourKeyUTC(r.start_date);
    if (mode.get(key) === "offered") continue; // already decided by a pending row
    if (r.status === "PENDING") {
      mode.set(key, "offered");
    } else if (!mode.has(key)) {
      mode.set(key, "accepted");
    }
  }
  return mode;
}

export const toNum = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

export const addSigned = (arr: number[], i: number | undefined, val: number, dir: Direction) => {
  if (i === undefined) return;
  if (dir === "UPWARD") arr[i] += val;
  else if (dir === "DOWNWARD") arr[i] += -Math.abs(val);
};
